import axios from "axios";
import { apiConfig } from "../../config/api";
import { getCurrentFCMToken } from "../../utils/fcmToken";
import secureStorage from "../../utils/secureStorage";

const API_BASE_URL = apiConfig.basePath;

export const signup = async ({ fullName, email, password }) => {
  try {
    const fcmToken = await getCurrentFCMToken();
    const headers = {
      "Accept-Language": "en",
    };
    
    if (fcmToken) {
      headers["FCM-Token"] = fcmToken;
    }
    
    const response = await axios.post(
      `${API_BASE_URL}signup`,
      {
        full_name: fullName,
        email: email,
        password: password,
      },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

export const login = async ({ email, password }) => {
  try {
    const fcmToken = await getCurrentFCMToken();
    const headers = {
      "Accept-Language": "en",
    };
    
    if (fcmToken) {
      headers["FCM-Token"] = fcmToken;
    }
    
    const response = await axios.post(
      `${API_BASE_URL}login`,
      {
        email,
        password,
        device_name: "W",
      },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

export const SocialLogin = async (body) => {
  const response = await axios.post(`${API_BASE_URL}social-login`, body);
  return response;
};

export const forgotPassword = async (body) => {
  const response = await axios.post(`${API_BASE_URL}forgot-password`, body);
  return response;
};

export const getProfile = async () => {
  const token = secureStorage.getItem("accessToken");
  const fcmToken = await getCurrentFCMToken();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Accept-Language": "en",
  };
  
  if (fcmToken) {
    headers["FCM-Token"] = fcmToken;
  }
  
  const response = await axios.get(`${API_BASE_URL}get-profile`, {
    headers,
  });
  return response;
};
// Upload image to backend and get URL
export const uploadImage = async (file) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only images (JPEG, PNG, GIF, WebP) are allowed.');
    }
    
    // Validate file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) {
      throw new Error('Invalid file extension. Only .jpg, .jpeg, .png, .gif, .webp are allowed.');
    }
    
    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size exceeds 5MB limit. Please choose a smaller file.');
    }
    
    // Validate file name (prevent path traversal)
    const { sanitizeFileName } = await import("../../utils/validation");
    const sanitizedFileName = sanitizeFileName(file.name);
    
    // Create new File object with sanitized name
    const sanitizedFile = new File([file], sanitizedFileName, { type: file.type });
    
    const token = secureStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append("files[]", sanitizedFile);
    const response = await axios.post(`${API_BASE_URL}s3-upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": "en",
      },
    });
    return response.data;
  } catch (error) {
    if (error.message && (error.message.includes('Invalid file') || error.message.includes('File size'))) {
      throw error; // Re-throw validation errors as-is
    }
    if (error.response) {
      throw new Error(error.response.data?.message || "Failed to upload image");
    } else if (error.request) {
      throw new Error("No response from server");
    } else {
      throw new Error("Error: " + error.message);
    }
  }
};

export const updateProfile = async (body) => {
  try {
    const token = secureStorage.getItem("accessToken");
    const response = await axios.post(`${API_BASE_URL}update-profile`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": "en",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteAdminsistrator = async (body) => {
  const response = await axios.post(`${API_BASE_URL}deleteAdministrator`, body);
  return response;
};

export const changePassword = async (body) => {
  try {
    const token = secureStorage.getItem("accessToken");
    const response = await axios.post(`${API_BASE_URL}change-password`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": "en",
      },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || "Failed to change password"
      );
    } else if (error.request) {
      throw new Error("No response from server");
    } else {
      throw new Error("Error: " + error.message);
    }
  }
};

export const logout = async () => {
  const token = secureStorage.getItem("accessToken");
  const fcmToken = await getCurrentFCMToken();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Accept-Language": "en",
  };
  
  if (fcmToken) {
    headers["FCM-Token"] = fcmToken;
  }
  
  const response = await axios.get(`${API_BASE_URL}logout`, {
    headers,
  });
  return response;
};

export const updateTheme = async ({ background_color }) => {
  const token = secureStorage.getItem("accessToken");
  const response = await axios.post(
    `${API_BASE_URL}theme-update`,
    {
      background_color,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": "en",
      },
    }
  );
  return response.data;
};

export const deleteAccount = async (password) => {
  try {
    const token = secureStorage.getItem("accessToken");
    
    // Require password for security (backend should verify)
    const payload = password ? { password, confirm_deletion: true } : { confirm_deletion: true };
    
    const response = await axios.post(
      `${API_BASE_URL}delete-account`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": "en",
        },
      }
    );
    
    // Verify deletion was successful
    if (response.data.success) {
      // Clear all client-side data
      secureStorage.clear();
      return response.data;
    }
    
    throw new Error(response.data.message || "Account deletion failed");
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || "Failed to delete account"
      );
    } else if (error.request) {
      throw new Error("No response from server");
    } else {
      throw new Error("Error: " + error.message);
    }
  }
};

/*
 * Get notifications with pagination
 */
export const getNotifications = async (page = 1) => {
  try {
    const token = secureStorage.getItem("accessToken");
    if (!token) throw new Error("No access token found");
    const response = await axios.post(
      `${API_BASE_URL}get-notification`,
      { page },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": "en",
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || "Failed to get notifications"
      );
    } else if (error.request) {
      throw new Error("No response from server");
    } else {
      throw new Error("Error: " + error.message);
    }
  }
};

/**
 * Mark a notification as read
 */
export const ReadNotification = async (notification_id) => {
  try {
    const token = secureStorage.getItem("accessToken");
    if (!token) throw new Error("No access token found");
    const response = await axios.post(
      `${API_BASE_URL}read-notification`,
      { notification_id },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": "en",
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || "Failed to mark notification as read"
      );
    } else if (error.request) {
      throw new Error("No response from server");
    } else {
      throw new Error("Error: " + error.message);
    }
  }
};

export const deleteNotification = async (notification_id) => {
  try {
    const token = secureStorage.getItem("accessToken");
    if (!token) throw new Error("No access token found");
    const response = await axios.post(
      `${API_BASE_URL}delete-notification`,
      { notification_id },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": "en",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
